import { DateRangePicker } from "../../components/ui/DateRangePicker";
import { Select } from "../../components/ui/Input";
import { END_OF_DAY, endsBeforeStart, type Schedule } from "../../lib/dates";

type ScheduleFieldProps = {
	value: Schedule;
	onChange: (next: Schedule) => void;
	timeZone: string;
	/** False while the browser's time zone is standing in for the shop's. */
	fromSettings: boolean;
	min?: Date | null;
	max?: Date | null;
	disabled?: boolean;
	placeholder?: string;
};

const START_HOURS = Array.from({ length: 24 }, (_, hour) => hour);

// Midnight is not offered as an end: an end at 12 AM on the 7th is the end of
// the 6th, which "End of day" on the 6th already says without the ambiguity.
const END_HOURS = [...Array.from({ length: 23 }, (_, index) => index + 1), END_OF_DAY];

const hourLabel = (hour: number): string => {
	if (hour === END_OF_DAY) return "End of day";
	const twelveHour = hour % 12 === 0 ? 12 : hour % 12;
	return `${twelveHour}:00 ${hour < 12 ? "AM" : "PM"}`;
};

export const ScheduleField = ({
	value,
	onChange,
	timeZone,
	fromSettings,
	min,
	max,
	disabled = false,
	placeholder,
}: ScheduleFieldProps) => (
	<div className="space-y-3">
		<DateRangePicker
			value={value.range}
			onChange={(range) => onChange({ ...value, range })}
			min={min}
			max={max}
			disabled={disabled}
			placeholder={placeholder}
		/>

		<div className="grid max-w-sm grid-cols-2 gap-3">
			<label className="block space-y-1.5">
				<span className="text-xs text-ink-muted">Starts at</span>
				<Select
					value={value.startHour}
					disabled={disabled || !value.range?.from}
					onChange={(event) => onChange({ ...value, startHour: Number(event.target.value) })}
				>
					{START_HOURS.map((hour) => (
						<option key={hour} value={hour}>
							{hourLabel(hour)}
						</option>
					))}
				</Select>
			</label>
			<label className="block space-y-1.5">
				<span className="text-xs text-ink-muted">Ends at</span>
				<Select
					value={value.endHour}
					disabled={disabled || !value.range?.to}
					onChange={(event) => onChange({ ...value, endHour: Number(event.target.value) })}
				>
					{END_HOURS.map((hour) => (
						<option key={hour} value={hour}>
							{hourLabel(hour)}
						</option>
					))}
				</Select>
			</label>
		</div>

		{endsBeforeStart(value) ? (
			<p className="text-xs text-alert">It ends before it starts. Pick a later end time.</p>
		) : null}

		<p className="text-xs text-ink-muted">
			{fromSettings
				? `Times are in ${timeZone}, your store's time zone.`
				: `Times are in ${timeZone}, this browser's time zone. Set your store's on the Settings page.`}
		</p>
	</div>
);
